import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { universityEndpoint } from "@/config/endpoints";
import { useAppSelector } from "@/stores/hook";

const PAGE_SIZE = 20;

/**
 * Infinite-paginated universities list, filtered by the redux
 * `universityFilter` slice. The query key includes `appliedFilters` so a
 * filter change invalidates the cache and refetches from page 1.
 *
 * Falls back to the legacy `useQuery` shape (`universities`, `length`) so
 * callers that just want a flat list (e.g. the `<UniversityFilter />`
 * dropdown) keep working unchanged.
 */
export default function useUniversities() {
  const appliedFilters = useAppSelector(
    (state) => state.universityFilter.appliedFilters
  );

  const query = useInfiniteQuery({
    queryKey: ["universities", "infinite", appliedFilters],
    queryFn: async ({ pageParam = 1 }) => {
      const params = {
        ...appliedFilters,
        page: pageParam,
        limit: PAGE_SIZE,
      };
      const res = await axiosInstance.get(universityEndpoint, { params });
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, allPages) => {
      const total = Number(lastPage?.meta?.total) || 0;
      const loaded = allPages.flatMap(
        (p: any) => p.universities || p.data || []
      ).length;
      if (loaded > 0 && loaded < total) return allPages.length + 1;
      const lastBatch = (lastPage?.universities || lastPage?.data || []).length;
      if (!total && lastBatch === PAGE_SIZE) return allPages.length + 1;
      return undefined;
    },
    select: (data) => {
      const universities = data.pages.flatMap(
        (p: any) => p.universities || p.data || []
      );
      return {
        universities,
        totalCount: Number(data.pages[0]?.meta?.total) || universities.length,
      };
    },
  });

  const universities = query.data?.universities ?? [];
  const totalCount = query.data?.totalCount ?? 0;

  const unversitySelects = universities.map((val: any) => ({
    value: String(val.id),
    name: val.name,
  }));

  return {
    universities,
    unversitySelects,
    length: totalCount,
    loadedCount: universities.length,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}

/**
 * Server-backed search for the Add/Edit Course university picker. Tenants
 * with 500+ universities can't ship the full list down to the client, so
 * the combobox debounces user input and asks the backend (`?search=…`).
 * Returns at most `limit` rows per request; the dropdown is meant for
 * search-as-you-type, not for browsing.
 */
export function useUniversitySearch(searchTerm: string, limit = 30) {
  const trimmed = searchTerm.trim();
  const query = useQuery({
    queryKey: ["universities", "search", trimmed, limit],
    queryFn: async () => {
      const params: Record<string, string | number> = { limit };
      if (trimmed) params.search = trimmed;
      const res = await axiosInstance.get(universityEndpoint, { params });
      return (res.data.universities ?? res.data.data ?? []) as any[];
    },
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });
  return {
    universities: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
}

/**
 * Single-university fetch used by the Add/Edit Course combobox to render
 * the selected university's label when it's not in the current search
 * page (e.g. when editing an existing course). Skipped when `id` is empty.
 */
export function useUniversityById(id?: string | number | null) {
  const numericId =
    id == null || id === "" ? null : Number.isFinite(Number(id)) ? Number(id) : null;
  const query = useQuery({
    queryKey: ["university", numericId],
    queryFn: async () => {
      const res = await axiosInstance.get(`${universityEndpoint}/${numericId}`);
      return (res.data.university ?? res.data.data ?? null) as any | null;
    },
    enabled: numericId != null,
    staleTime: 5 * 60 * 1000,
  });
  return { university: query.data ?? null, isLoading: query.isLoading };
}

/**
 * Lightweight, unpaginated fetch used by selectors / dropdowns that need
 * the full list at once (e.g. the "Filter by University" picker inside
 * the courses filter modal). Keeps the legacy non-infinite behaviour the
 * old `useUniversities` provided.
 */
export function useUniversityOptions(
  /**
   * Extra server-side filters (e.g. `cf_<countryFieldId>=12,13` from the
   * Study Preferences cascade — universities carrying any of the selected
   * CF values; or `forChildId=<id>` to scope to a deal's country). Part of
   * the query key, so each filter combination caches independently; omit for
   * the full list (legacy behaviour).
   */
  extraParams?: Record<string, string>,
  /**
   * `enabled` defers the fetch until it's actually needed (e.g. only when the
   * University changer popover is OPEN). Defaults to true so existing callers
   * are unchanged.
   */
  opts?: { enabled?: boolean },
) {
  const query = useQuery({
    queryKey: ["universities", "options", extraParams ?? {}],
    queryFn: async () => {
      const res = await axiosInstance.get(universityEndpoint, {
        params: { limit: 500, ...(extraParams ?? {}) },
      });
      return res.data.universities ?? res.data.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: opts?.enabled ?? true,
  });

  const unversitySelects =
    query.data?.map((val: any) => ({
      value: String(val.id),
      name: val.name,
    })) ?? [];

  return {
    universities: query.data ?? [],
    unversitySelects,
    isLoading: query.isLoading,
  };
}
